import {
  functionalUpdate,
  type Row,
  type TableOptions,
  type Updater,
} from '@tanstack/react-table';
import { useCallback, useEffect, useRef } from 'react';

import type {
  DataTableRowActivationEvent,
  DataTableRowInteractivityProps,
} from './row-interactivity-types.js';
import { useCallbackRef } from '../../../../core/hooks/useCallbackRef.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * Configuration hook for the rowInteractivity in the DataTable.
 * @internal
 */
export function useRowInteractivity<TData extends DataTableRowData>(
  props: DataTableRowInteractivityProps<TData>,
  options: TableOptions<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const {
    activeRow: activeRowProp,
    defaultActiveRow,
    onActiveRowChange,
    interactiveRows,
  } = props;

  const [activeRow, setActiveRow] = useControllableState<string | null>({
    value: activeRowProp,
    defaultValue: defaultActiveRow,
  });
  options.enableRowInteractivity =
    interactiveRows !== undefined && interactiveRows !== false;

  // DEPRECATION: [APPDEV-15075] BREAKING-CHANGE: Remove rowInteractivity link
  const getLinkForRow = useCallback(
    (row: Row<TDataTableFeatures, Exclude<TData, unknown>>) => {
      if (
        interactiveRows !== undefined &&
        typeof interactiveRows === 'object' &&
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        interactiveRows.link
      ) {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        return interactiveRows.link(row.original);
      }
      return undefined;
    },
    [interactiveRows],
  );

  const onActiveRowChangeProp = useCallbackRef(onActiveRowChange);

  const onActiveRowChangeCallback = useCallback(
    (updater: Updater<string | null>, event?: DataTableRowActivationEvent) => {
      const nextState = functionalUpdate(updater, activeRow ?? null);

      setActiveRow(nextState);
      onActiveRowChangeProp(nextState, event);
    },
    [setActiveRow, onActiveRowChangeProp, activeRow],
  );

  if (options.enableRowInteractivity) {
    options.onActiveRowChange = onActiveRowChangeCallback;
    options.enableAutoActivation =
      interactiveRows &&
      (interactiveRows === true || interactiveRows.autoActivate !== false);

    // DEPRECATION: [APPDEV-15075] BREAKING-CHANGE: Remove rowInteractivity link
    options.getLinkForRow = getLinkForRow;
  }

  // Auto reset the state if the data changes
  const firstRun = useRef(true);
  useEffect(
    () => {
      if (firstRun.current) {
        firstRun.current = false;
        return;
      }

      if (options.enableRowInteractivity) {
        options.onActiveRowChange?.(null);
      }
    },
    // We only want to listen to the individual options parts and not the entire options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.enableRowInteractivity, options.data],
  );

  options.state = {
    ...options.state,
    activeRow: activeRow ?? null,
  };
}
